import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TTeamManagementForm, UseCreateTeamReturn } from '../team-management.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { StatusEnum, TeamsInterface, useCreateTeamsMutation } from '@/types/generated/graphql';
import { notification } from 'antd';
import { cacheAddTeamV2 } from '../team-handlers';

const defaultValues = {
  name: '',
  members: [],
};

export const useCreateTeam = (): UseCreateTeamReturn => {
  const { t } = useTranslation();
  const [openAddModal, setOpenAddModal] = useState(false);

  const handleOpenAdd = () => setOpenAddModal(true);
  const handleCloseAdd = () => setOpenAddModal(false);

  const createForm = useForm<TTeamManagementForm>({
    resolver: zodResolver(
      z.object({
        name: z
          .string({ required_error: t('name_is_required') })
          .trim()
          .min(1, t('name_is_required')),
        members: z
          .array(z.number({ required_error: t('each_member_id_must_be_number') }))
          .min(1, { message: t('at_least_one_member_is_required') }),
      })
    ),
    defaultValues,
  });

  const { handleSubmit: handleCreateSubmit, control: addTeamControl, reset } = createForm;

  const handleOpenAddModal = () => {
    handleOpenAdd();
  };

  const handleCloseAddModal = () => {
    handleCloseAdd();
    reset(defaultValues);
  };

  const [createTeam, { loading: createTeamLoading }] = useCreateTeamsMutation({
    onCompleted: (res) => {
      notification.success({ message: res.createTeams?.message });
      cacheAddTeamV2(res.createTeams?.data as TeamsInterface);
      handleCloseAddModal();
    },
    onError: (error) => {
      notification.error({ message: error?.message });
    },
  });

  const handleCreateTeamSubmit = handleCreateSubmit(async function submitHandler(data: TTeamManagementForm) {
    createTeam({
      variables: {
        input: {
          name: data.name,
          inputTeamMembersInterface: data.members.map((userId) => ({
            userId: parseInt(userId),
            status: StatusEnum.active,
          })),
          description: 'n/a',
          status: StatusEnum.active,
        },
      },
    });
  });

  return {
    openAddModal,
    handleOpenAdd,
    handleCloseAdd,
    handleOpenAddModal,
    handleCloseAddModal,
    createForm,
    createTeam,
    addTeamControl,
    handleCreateTeamSubmit,
    createTeamLoading,
  };
};
